// 处理React 18中的findDOMNode警告
import { ProgressTip1 } from '@/components/RightContent';

if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // 过滤掉findDOMNode相关的警告
    if (typeof args[0] === 'string' && args[0].includes('findDOMNode is deprecated')) {
      return;
    }
    originalConsoleError(...args);
  };
}

import { AvatarDropdown, AvatarName, } from '@/components';
import { profile, queryCurrentUser, getPermissionTree } from '@/services/ant-design-pro/api';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { App as AntdApp } from 'antd';
import defaultSettings from '../config/defaultSettings';
import logoIcon from '../public/imgs/logo.svg';
import userIcon from '../public/imgs/user.png';
import { errorConfig } from './requestErrorConfig';
import { useModel } from 'umi';
import { useLayoutEffect, useEffect, useRef } from 'react';

const isDev = process.env.NODE_ENV === 'development';
import { devModeOverride, checkDevModeClick, getAndClearModeChanged } from '@/utils/devMode';
import { clearVideoListFilter } from '@/utils/pagination';
const loginPath = '/login';

const noAuthPaths = ['/login', '/user/login', '/user/logout', '/401', '/404'];

const getEnvTagText = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  const { hostname, href } = window.location;

  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'DEV';
  }

  if (hostname.includes('sra-test')) {
    return 'TEST';
  }

  return '';
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  permissionTree?: any;
}> {
  const fetchUserInfo = async () => {
    // try {
    //   console.log('获取用户信息...isDev:'+isDev);
    //   if (isDev) {
    //     // await queryCurrentUser({ skipErrorHandler: true });  
    //   }
    //   const res = await profile();
    //   const data = res?.data;
    //   if (!data) return undefined;
    //   return {
    //     name: data.staffNameEn || data.staffNameCn || data.staffNo,
    //     userid: data.staffNo,
    //   } as API.CurrentUser;
    // } catch (error) {
    //   console.error('获取用户信息失败:', error);
    //   if (!isDev) {
    //     window.location.href = '/app/oauth2/v1/authorize';
    //   }
    //   // // 重定向到登录页面，但保留原始路径作为参数
    //   // history.push({
    //   //   pathname: loginPath,
    //   //   search: `?redirect=${encodeURIComponent(location.pathname + location.search)}`
    //   // });
    // }
    return undefined;
  };
  let permissionTree: any = ['/home'];

  console.log('history:', history);
  // 如果不是登录页面，执行
  const { location } = history;
  console.log('当前路径:', location.pathname);
  const noFetchPaths = ['/login', '/401', '/404'];
  if (location.pathname !== loginPath && !noFetchPaths.includes(location.pathname)) {
    const currentUser = await fetchUserInfo();
    // const { user, setUserInfo } = useModel('user');
    // try {
    //   const res = await getPermissionTree({ skipErrorHandler: true });
    //   console.log('菜单权限树返回:', res);
    //   permissionTree = res?.data;
    //   setUserInfo({
    //     ...user,
    //     permissionTree,
    //   });
    // } catch (e) {
    //   console.error('获取菜单权限树失败:', e);
    //   permissionTree = ['/home'];
    // }
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      permissionTree,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    permissionTree,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const { user, setUserInfo } = useModel('user');
  const envTagText = getEnvTagText();

  useLayoutEffect(() => {
    if (!user?.token) {
      const storedUserInfo = localStorage.getItem('userInfo');
      if (storedUserInfo) {
        try {
          const userInfo = JSON.parse(storedUserInfo);
          if (userInfo?.token) {
            setUserInfo(userInfo);
          }
        } catch (e) {
          console.error('Failed to parse stored user info:', e);
        }
      }
    }
  }, []);

  // 跨页面缓存清理：监听路由变化，一旦从「带缓存的列表页」跳到非该区域，
  // 就把 localStorage 里那条列表缓存清掉，下次点回菜单进入时恢复成初始状态。
  // 现在挂的是 video 列表缓存；后续再加其他带缓存列表页，往 CACHEABLE_SEGMENTS 里加就行。
  // 用「路径段包含」匹配（按 / 切段，看是否含目标 segment），同时兼容带/不带 base 的两种形态，
  // 又不会被 /videoaudit 这种后缀穿帮。
  const lastPathRef = useRef<string>(history.location.pathname || '/');
  useEffect(() => {
    const CACHEABLE_SEGMENTS = ['video'];
    const isInCacheableArea = (p: string) =>
      CACHEABLE_SEGMENTS.some((seg) => p.split('?')[0].split('/').includes(seg));
    const unlisten = history.listen(({ location }) => {
      const was = lastPathRef.current;
      const is = location.pathname || '/';
      lastPathRef.current = is;
      // 离开 video 区才清：列表 ↔ 详情（同区）不算离开
      if (isInCacheableArea(was) && !isInCacheableArea(is)) {
        clearVideoListFilter();
      }
    });
    return () => unlisten && unlisten();
  }, []);

  return {
    actionsRender: () => [
      <ProgressTip1 key="ProgressTip1" />,
      // <BellTip key="BellTip" />,
    ],
    // actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: userIcon,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      console.log('当前路径:', location.pathname);
      
      const isNoAuthPath = noAuthPaths.some(path => 
        location.pathname === path || location.pathname.startsWith(path + '/')
      );
      
      if (isNoAuthPath) {
        return;
      }
      
      console.log('user:', user);
      const hasUser = user?.token && user.token !== '';
      const hasInitialStateUser = initialState?.currentUser && Object.keys(initialState.currentUser).length > 0;
      const hasLocalToken = localStorage.getItem('token');
      
      if (!hasUser && !hasInitialStateUser && !hasLocalToken) {
        console.log('用户未登录，跳转到登录页');
        let redirect = location.pathname ? location.pathname.replace('/knowledge', '') : '/';
        history.push({
          pathname: loginPath,
          search: `?redirect=${encodeURIComponent(redirect)}`
        });
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [
          // <Link
          //   key="manage"
          //   // to="https://manage.group"
          //   target="_blank"
          //   to={'#'}
          // >
          //   <span>@Manage Group</span>
          // </Link>,
        ]
      ,
    menuDataRender: (menuData) => {
      console.log('菜单权限树:', menuData);
      console.log('user:', user);
      console.log('initialState permissionTree:', initialState?.permissionTree);
      let filterMenuData = [];
      // 优先使用user中的permissionTree（登录时获取的最新数据），其次使用initialState中的（刷新时获取的）
      const permissionTree = (user?.permissionTree || []) || (initialState?.permissionTree || []);
      if (permissionTree && permissionTree.length > 0 ) {
        // 先对permissionTree按sort字段排序
        const sortedPermissionTree = [...permissionTree].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
        
        // 按照排序后的permissionTree顺序，将菜单加入filterMenuData
        filterMenuData = sortedPermissionTree
          .map(perm => menuData.find(item => item.path === perm.route))
          .filter(Boolean);
        
        // 开发模式覆盖时额外检查是否有/knowledge, /ai-chat, /parameter菜单
        if (devModeOverride) {
          const knowledgeMenu = menuData.find(item => item.path === '/knowledge');
          if (knowledgeMenu && !filterMenuData.some(item => item.path === '/knowledge')) {
            filterMenuData.push(knowledgeMenu);
          }
          const aiChatMenu = menuData.find(item => item.path === '/ai-chat');
          if (aiChatMenu && !filterMenuData.some(item => item.path === '/ai-chat')) {
            filterMenuData.push(aiChatMenu);
          }
          const parameterMenu = menuData.find(item => item.path === '/parameter');
          if (parameterMenu && !filterMenuData.some(item => item.path === '/parameter')) {
            filterMenuData.push(parameterMenu);
          }
        }
      } else {
        filterMenuData = menuData;
      }
      console.log('filterMenuData:', filterMenuData);

      return filterMenuData;
    },
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      const MessageHolder = () => {
        const { message } = AntdApp.useApp();
        (window as any).__ant_message = message;
        return null;
      };
      return (
        <AntdApp>
          <MessageHolder />
          {children}
        </AntdApp>
      );
    },
    ...initialState?.settings,
    logo: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img src={logoIcon} />
        <span
          style={{ marginLeft: 8, fontSize: 16, fontWeight: 600, color: '#1890ff', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            checkDevModeClick();
            // 检查模式是否切换并显示提示
            setTimeout(() => {
              const changed = getAndClearModeChanged();
              if (changed !== null) {
                window.localStorage?.setItem('devModeOverride', changed ? 'true' : 'false');
                // 使用全局 message 显示提示
                const msg = (window as any).__ant_message;
                if (msg) {
                  msg.success(`开发者模式已${changed ? '开启' : '关闭'}`);
                }
              }
            }, 0);
          }}
        >
          EduAdmin
        </span>
        {envTagText ? (
          <span
            style={{
              marginLeft: 8,
              padding: '0 8px',
              borderRadius: 4,
              backgroundColor: envTagText === 'TEST' ? '#faad14' : '#52c41a',
              color: '#fff',
              fontSize: 12,
              lineHeight: '20px',
              height: 20,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {envTagText}
          </span>
        ) : null}
      </div>
    ),
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
